import { useSyncExternalStore } from 'react';
import { isoDate } from '@cf/shared';
import type { IsoDate } from '@cf/shared';
import { RULE_SETS_BY_START_YEAR, formatTaxYearLabel, rulesForTaxYear } from '@cf/tax';
import { APP_CLOCK } from './appClock';

/**
 * The RULES BASIS an adviser is working on: which tax year's rule set every
 * projection in this session runs under.
 *
 * The default is always the CURRENT tax year (from APP_CLOCK — today's date,
 * clamped to the newest shipped rule set). The one alternative is the
 * PREVIOUS year, kept selectable because prior-year work stays live long
 * after 6 April — a 2025/26 self-assessment can be filed until
 * 31 January 2027. Switching basis is a deliberate, visible act: the badge in
 * the app bar names the active rule set at all times, and turns amber the
 * moment the session leaves the current year.
 *
 * The engine refuses a start year earlier than the tax year containing the
 * as-of date (`start-year-before-as-of-date` is a BLOCKING validation), so a
 * basis is an (asOfDate, startYear) PAIR: the previous-year basis anchors its
 * as-of date to the final day of that tax year (5 April), i.e. "the position
 * as at the end of the year being assessed".
 *
 * Selection is session-scoped on purpose (sessionStorage, not localStorage):
 * a new browser session always opens on the current year's rules. An adviser
 * must re-choose last year each session rather than silently inheriting it.
 */

export type TaxRulesBasisKey = 'current' | 'previous';

export interface TaxRulesBasisOption {
  readonly key: TaxRulesBasisKey;
  /** Tax-year start year, e.g. 2026 for 2026/27. */
  readonly startYear: number;
  /** The as-of anchor paired with the year (today, or 5 April for a past year). */
  readonly asOfDate: IsoDate;
  /** "2026/27" — the display name used everywhere the basis is shown. */
  readonly taxYearLabel: string;
  /** The versioned rule-set identity actually applied, e.g. "2026-27.4". */
  readonly rulesVersion: string;
  /** Lay one-liner: when and why an adviser would pick this basis. */
  readonly note: string;
}

const STORAGE_KEY = 'cf.tax-rules-basis';

function buildOptions(): readonly TaxRulesBasisOption[] {
  const current: TaxRulesBasisOption = {
    key: 'current',
    startYear: APP_CLOCK.startYear,
    asOfDate: APP_CLOCK.asOfDate,
    taxYearLabel: formatTaxYearLabel(APP_CLOCK.startYear),
    rulesVersion: rulesForTaxYear(APP_CLOCK.startYear).version,
    note: 'The current tax year — the default for all new modelling.',
  };
  const previousYear = APP_CLOCK.startYear - 1;
  if (RULE_SETS_BY_START_YEAR[previousYear] === undefined) {
    return [current];
  }
  // Self-assessment for a tax year ending 5 April Y closes on 31 January Y+1.
  const filingDeadlineYear = previousYear + 2;
  const previous: TaxRulesBasisOption = {
    key: 'previous',
    startYear: previousYear,
    // The last day of that tax year: the engine blocks a start year earlier
    // than the as-of date's tax year, and "position at year end" is the
    // honest anchor for work on a closed year.
    asOfDate: isoDate(`${String(previousYear + 1)}-04-05`),
    taxYearLabel: formatTaxYearLabel(previousYear),
    rulesVersion: rulesForTaxYear(previousYear).version,
    note:
      `The previous tax year, for prior-year work — ${formatTaxYearLabel(previousYear)} ` +
      `self-assessment can be filed until 31 January ${String(filingDeadlineYear)}. ` +
      `Figures show the position as at 5 April ${String(previousYear + 1)} (the final day of ` +
      `that year): the first projected year is ${formatTaxYearLabel(previousYear)} and ages ` +
      'are taken at that date, so charts shift a year against the current-year view.',
  };
  return [current, previous];
}

/** All selectable bases (always includes 'current'; 'previous' only when shipped). */
export const TAX_RULES_BASIS_OPTIONS: readonly TaxRulesBasisOption[] = buildOptions();

function readStored(): TaxRulesBasisKey {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === 'previous' && TAX_RULES_BASIS_OPTIONS.some((o) => o.key === 'previous')) {
      return 'previous';
    }
  } catch {
    // Storage unavailable (privacy mode, tests without a DOM) → default.
  }
  return 'current';
}

let selectedKey: TaxRulesBasisKey = readStored();
const listeners = new Set<() => void>();

/** The active basis — data clients read this at call time. */
export function getTaxRulesBasis(): TaxRulesBasisOption {
  return TAX_RULES_BASIS_OPTIONS.find((o) => o.key === selectedKey) ?? TAX_RULES_BASIS_OPTIONS[0]!;
}

export function setTaxRulesBasis(key: TaxRulesBasisKey): void {
  const next = TAX_RULES_BASIS_OPTIONS.some((o) => o.key === key) ? key : 'current';
  if (next === selectedKey) return;
  selectedKey = next;
  try {
    if (next === 'current') sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Storage unavailable — the in-memory selection still applies.
  }
  for (const listener of listeners) listener();
}

export function subscribeTaxRulesBasis(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * React subscription to the active basis. Components showing figures MUST
 * either use this hook or receive the basis via props, so a switch re-renders
 * (and re-runs) everything on screen — a stale chart under the wrong year's
 * rules is exactly what this store exists to prevent.
 */
export function useTaxRulesBasis(): TaxRulesBasisOption {
  return useSyncExternalStore(subscribeTaxRulesBasis, getTaxRulesBasis, getTaxRulesBasis);
}
